import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const EditCountry: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('countries');
  const isRTL = i18n.language === 'ar';



  const [formData, setFormData] = useState({
    name: "",
    currencyId: 0,
    countryType: 0,
    symbol: "",
    phoneCode: "",
    phoneRegularExpression: "",
    warehouseAddressLine1: "",
    warehouseAddressLine2: "",
    warehouseCityName: "",
    warehousePostalCode: "",
    warehousePhone: "",
    isActive: true,
    isSuspended: false,
    id: id,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [currencies, setCurrencies] = useState<any[]>([]);
  // Fetch currencies list
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await adminApi.call('/currency', 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('messages.fetchError'));
          return;
        }

        setCurrencies(response.data?.items);
      } catch (error: any) {
        showErrorToast(t('messages.fetchError') + ": " + error.message);
      }
    };

    fetchCurrencies();
  }, []);


  // Fetch existing item
  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await adminApi.call(`/country/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('messages.fetchError'));
          return;
        }

        setFormData(response.data);
      } catch (error: any) {
        showErrorToast(t('messages.fetchError') + ": " + error.message);
      }
    };

    fetchItem();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name.includes("countryType") || name.includes("currencyId") ? Number(value) : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Validate name - extract English value from JSON if present
    let nameValue = formData.name;
    try {
      if (formData.name && formData.name.startsWith('{')) {
        const parsed = JSON.parse(formData.name);
        nameValue = parsed.en || '';
      }
    } catch (e) {
      // Not valid JSON, use as-is
    }

    if (!nameValue?.trim()) newErrors.name = t('countries:validation.nameRequired');
    if (!formData.symbol?.trim()) newErrors.symbol = t('countries:validation.symbolRequired');
    if (!formData.currencyId) newErrors.currencyId = t('countries:validation.currencyRequired');
    // Warehouse fields are now optional - no validation required
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitUpdate = async () => {
    if (!validateForm()) {
      showErrorToast(t('countries:validation.fillAllFields'));
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/country/admin/Update', 'PUT', formData);

      if (!response.success) {
        showErrorToast(response.error || t('messages.updateError'));
        return;
      }

      showSuccessToast(t('messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('messages.updateError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('breadcrumbs.countries'), path: '/countries' },
    { label: t('breadcrumbs.edit') }
  ];

  return (
    <PageLayout
      title={t('editCountry')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-md-6">
            <MultiLanguageTextInput
              label={t('fields.name')}
              value={formData.name}
              onChange={(value) => {
                setFormData({ ...formData, name: value });
                if (errors.name) {
                  setErrors(prev => ({ ...prev, name: '' }));
                }
              }}
              placeholder={t('placeholders.name')}
              error={errors.name}
              disabled={loading}
              required
              isRTL={isRTL}
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.symbol')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.symbol ? 'is-invalid' : ''}`}
              name="symbol"
              value={formData.symbol}
              onChange={handleInputChange}
              placeholder={t('placeholders.symbol')}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            />
            {errors.symbol && <div className="invalid-feedback">{errors.symbol}</div>}
          </div>

          {/* Phone Code and Regular Expression - Hidden for Source Country (type 2) */}
          {formData.countryType !== 2 && (
            <>
              <div className="col-md-6">
                <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
                  {t('fields.phoneCode')}
                </label>
                <input
                  type="text"
                  className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.phoneCode ? 'is-invalid' : ''}`}
                  name="phoneCode"
                  value={formData.phoneCode || ''}
                  onChange={handleInputChange}
                  placeholder={t('placeholders.phoneCode')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
                {errors.phoneCode && <div className="invalid-feedback">{errors.phoneCode}</div>}
              </div>

              <div className="col-md-6">
                <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
                  {t('fields.phoneRegularExpression')}
                </label>
                <input
                  type="text"
                  className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
                  name="phoneRegularExpression"
                  value={formData.phoneRegularExpression || ''}
                  onChange={handleInputChange}
                  placeholder={t('placeholders.phoneRegularExpression')}
                  dir="ltr"
                />
              </div>
            </>
          )}

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.currency')}
            </label>
            <select
              className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
              name="currencyId"
              value={formData.currencyId || ""}
              onChange={handleInputChange}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value="">{t('common:select')}</option>
              {currencies.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name + " (" + item.lastExchangeRate + ")"}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.type')}
            </label>
            <select
              key={formData.countryType}
              className={`form-select ${isRTL ? 'text-end' : 'text-start'}`}
              name="countryType"
              value={formData.countryType}
              onChange={handleInputChange}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <option value={0}>{t('types.both')}</option>
              <option value={1}>{t('types.destination')}</option>
              <option value={2}>{t('types.source')}</option>
            </select>
          </div>
{formData.countryType !== 1 && (
              <>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.isActive')}
            </label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                className="form-check-input"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              <label className={`form-check-label ${isRTL ? 'text-end' : 'text-start'}`} htmlFor="isActive">
                {formData.isActive ? t('status.active') : t('status.inactive')}
              </label>
            </div>
          </div>
</>
            )}
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.isSuspended')}
            </label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                id="isSuspended"
                name="isSuspended"
                className="form-check-input"
                checked={formData.isSuspended}
                onChange={(e) => setFormData(prev => ({ ...prev, isSuspended: e.target.checked }))}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              <label className={`form-check-label ${isRTL ? 'text-end' : 'text-start'}`} htmlFor="isSuspended">
                {formData.isSuspended ? t('status.suspended') : t('status.notSuspended')}
              </label>
            </div>
          </div>

          {/* Warehouse Address Section */}
          { formData.countryType != 1 && (
            <>
            <div className="col-12">
            <h5 className={`form-section-title ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.warehouseAddress')}
            </h5>
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.warehouseAddressLine1')}
              <ActionButton
                variant="primary"
                size="sm"
                className={`${isRTL ? 'me-2' : 'ms-2'}`}
                onClick={() => {
                  const input = document.querySelector('input[name="warehouseAddressLine1"]') as HTMLInputElement;
                  if (input) {
                    const start = input.selectionStart || 0;
                    const end = input.selectionEnd || 0;
                    const newValue = formData.warehouseAddressLine1.slice(0, start) + "@customer_id@" + formData.warehouseAddressLine1.slice(end);
                    setFormData({ ...formData, warehouseAddressLine1: newValue });
                    setTimeout(() => {
                      input.focus();
                      input.selectionStart = input.selectionEnd = start + "@customer_id@".length;
                    }, 0);
                  }
                }}
              >
                customer_id
              </ActionButton>
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="warehouseAddressLine1"
              value={formData.warehouseAddressLine1}
              onChange={handleInputChange}
              placeholder={t('placeholders.warehouseAddressLine1')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
                <div className="col-md-6"></div>
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.warehouseAddressLine2')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="warehouseAddressLine2"
              value={formData.warehouseAddressLine2}
              onChange={handleInputChange}
              placeholder={t('placeholders.warehouseAddressLine2')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
                <div className="col-md-6"></div>

          <div className="col-md-4">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.warehouseCity')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="warehouseCityName"
              value={formData.warehouseCityName}
              onChange={handleInputChange}
              placeholder={t('placeholders.warehouseCity')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          <div className="col-md-4">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.warehousePostalCode')}
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="warehousePostalCode"
              value={formData.warehousePostalCode}
              onChange={handleInputChange}
              placeholder={t('placeholders.warehousePostalCode')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          <div className="col-md-4">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.warehousePhone')}
            </label>
            <input
              type="tel"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="warehousePhone"
              value={formData.warehousePhone}
              onChange={handleInputChange}
              placeholder={t('placeholders.warehousePhone')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
            </>
          ) }


        </div>

        <div className="text-center mt-4">
          <ActionButton
            variant="secondary"
            onClick={() => navigate('/countries')}
            className={isRTL ? 'ms-2' : 'me-2'}
          >
            <i className={`fa ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'} ${isRTL ? 'ms-1' : 'me-1'}`}></i>
            {t('actions.back')}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={submitUpdate}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('actions.updating') : t('actions.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
